import React, { useState } from 'react';
import { Button } from 'react-bootstrap';
import { css } from '@emotion/react';
import t from 'react-translate';

import { config } from '@config/pendo.config.json';
import NvIcon from 'shared/components/nv-icon';
import SaveToast from 'athena/components/mentoring-program/program-configuration/save-toast';
import { SaveStatus } from 'redux/schemas/app/mentoring-programs';
import { copyToClipboard } from 'shared/react-utils';
import {
  black,
  gray3,
  gray5,
  gray6,
  primary,
  white,
} from 'styles/global_defaults/colors';
import {
  headerLineHeight,
  normalFontWeight,
  openSans,
  semiBoldFontWeight,
  textMediumFontSize,
  textMediumLineHeight,
  textSmallLineHeight,
} from 'styles/global_defaults/fonts';
import {
  halfSpacing,
  quarterSpacing,
  standardSpacing,
} from 'styles/global_defaults/scaffolding';

const COPIED_TOAST_MS = 2000;

type Props = {
  feedback?: string;
  isGenerating: boolean;
};

const styles = css`
  display: flex;
  flex-direction: column;

  .draft-header {
    display: flex;
    align-items: center;
    gap: ${halfSpacing}px;
    padding: ${halfSpacing}px 0;
    border-bottom: 1px solid ${gray5};
  }

  .draft-icon {
    width: 36px;
    height: 36px;
    border-radius: 18px;
    background-color: ${gray6};
    color: ${gray3};
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  .draft-label {
    margin: 0;
    font-family: ${openSans};
    font-weight: ${semiBoldFontWeight};
    font-size: ${textMediumFontSize}px;
    line-height: ${textSmallLineHeight}px;
    color: ${black};
  }

  .copy-button {
    display: flex;
    align-items: center;
    gap: ${quarterSpacing}px;
    padding: ${quarterSpacing}px 0;
    color: ${primary};
    font-family: ${openSans};
    font-weight: ${normalFontWeight};
    font-size: ${textMediumFontSize}px;
    line-height: ${textMediumLineHeight}px;
  }

  .draft-body {
    margin-top: ${halfSpacing}px;
    padding: ${halfSpacing}px ${halfSpacing}px ${halfSpacing}px ${standardSpacing}px;
    background: ${white};
    border: 1px dashed ${gray3};
    border-radius: ${halfSpacing}px;
    font-family: ${openSans};
    font-weight: ${normalFontWeight};
    font-size: ${textMediumFontSize}px;
    line-height: ${headerLineHeight}px;
    color: ${black};
    white-space: pre-wrap;
  }
`;

const DraftFeedback: React.FC<Props> = ({ feedback, isGenerating }) => {
  const [showToast, setShowToast] = useState(false);

  const sectionDataQa = config.pendo.submissionView.aiInsights.sectionDraftFeedback;
  const copyDataQa = config.pendo.submissionView.aiInsights.copyFeedback;

  const handleCopy = () => {
    if (!feedback) return;
    copyToClipboard(feedback);
    setShowToast(true);
    setTimeout(() => setShowToast(false), COPIED_TOAST_MS);
  };

  const bodyContent = isGenerating
    ? t.SUBMISSIONS.AI_ASSIGNMENT.GENERATING()
    : feedback;

  if (!bodyContent) {
    return null;
  }

  return (
    <div css={styles} data-qa={sectionDataQa}>
      <div className='draft-header'>
        <div className='draft-icon'>
          <NvIcon icon='drafts' size='smallest' aria-hidden />
        </div>
        <h3 className='draft-label'>
          {t.SUBMISSIONS.AI_ASSIGNMENT.DRAFT_FEEDBACK()}:
        </h3>
        {showToast ? (
          <SaveToast status={SaveStatus.COPIED} />
        ) : (
          <Button
            variant='link'
            className='copy-button'
            onClick={handleCopy}
            disabled={isGenerating}
            aria-label={t.SUBMISSIONS.AI_ASSIGNMENT.COPY()}
            data-qa={copyDataQa}
          >
            <NvIcon size='xss-smallest' icon='files' />
            {t.SUBMISSIONS.AI_ASSIGNMENT.COPY()}
          </Button>
        )}
      </div>
      <div className='draft-body'>{bodyContent}</div>
    </div>
  );
};

export default DraftFeedback;
